import React, { memo, useState } from 'react';
import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import {
    TASK_FIELDS,
    STATUS_META,
    PRIORITY_META,
    IMPORTANCE_META,
    DIFFICULTY_META,
} from './taskMeta';

/**
 * The single chip vocabulary for task attributes.
 *
 * Read-only by default; pass `onChange` to turn a chip into a popover picker.
 * A picker beats the old click-to-cycle behaviour here: it shows every option at
 * once and gives a 40px tap target on mobile instead of requiring three taps to
 * get from "low" back to "low".
 */

const chipBase =
    'inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border whitespace-nowrap';

function BaseChip({ field, value, size = 'sm', className }) {
    const config = TASK_FIELDS[field];
    const meta = config.meta[value] ?? config.meta[Object.keys(config.meta)[0]];
    const label = config.prefix ? `${config.prefix}:${meta.label}` : meta.label;

    return (
        <span
            className={cn(
                chipBase,
                meta.chip,
                size === 'xs' && 'text-[9px] px-1.5 py-0',
                className,
            )}
        >
            {label}
        </span>
    );
}

/**
 * A chip that opens a value picker. Falls back to a plain chip when read-only.
 */
function FieldPickerImpl({ field, value, onChange, disabled = false, size = 'sm', className, testId }) {
    const [open, setOpen] = useState(false);
    const config = TASK_FIELDS[field];

    if (disabled || !onChange) {
        return <BaseChip field={field} value={value} size={size} className={className} />;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={testId ?? `task-${field}-picker`}
                    title={`Change ${config.label.toLowerCase()}`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                        'rounded-full transition-transform duration-150 hover:brightness-125 active:scale-95',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                        open && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
                    )}
                >
                    <BaseChip field={field} value={value} size={size} className={className} />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                sideOffset={6}
                className="w-44 p-1.5 bg-black/90 backdrop-blur-xl border-white/10 rounded-xl"
            >
                <p className="px-2 py-1 text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    {config.label}
                </p>
                <div className="mt-1 space-y-0.5">
                    {config.options.map((option) => {
                        const meta = config.meta[option];
                        const isActive = option === value;
                        return (
                            <button
                                key={option}
                                type="button"
                                data-testid={`task-${field}-option-${option}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(false);
                                    if (option !== value) onChange(option);
                                }}
                                className={cn(
                                    'w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-left transition-colors',
                                    isActive ? 'bg-white/10' : 'hover:bg-white/5',
                                )}
                            >
                                <BaseChip field={field} value={option} />
                                {isActive ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export const FieldPicker = memo(FieldPickerImpl);

export const StatusChip = memo(({ value, ...rest }) => <BaseChip field="status" value={value} {...rest} />);
export const PriorityChip = memo(({ value, ...rest }) => <BaseChip field="priority" value={value} {...rest} />);
export const ImportanceChip = memo(({ value, ...rest }) => <BaseChip field="importance" value={value} {...rest} />);
export const DifficultyChip = memo(({ value, ...rest }) => <BaseChip field="difficulty" value={value} {...rest} />);

/**
 * The P / I / D trio, rendered as pickers when editable.
 * `fields` lets a caller drop one (the changeset rows show status separately).
 */
export const TaskAttributeRow = memo(function TaskAttributeRow({
    task,
    onUpdate,
    disabled = false,
    fields = ['priority', 'importance', 'difficulty'],
    size = 'sm',
    className,
}) {
    return (
        <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
            {fields.map((field) => (
                <FieldPicker
                    key={field}
                    field={field}
                    size={size}
                    value={task[field] || 'medium'}
                    disabled={disabled}
                    onChange={onUpdate ? (next) => onUpdate({ [field]: next }) : undefined}
                    testId={`task-${task.id}-${field}`}
                />
            ))}
        </div>
    );
});

export { PRIORITY_META, IMPORTANCE_META, DIFFICULTY_META, STATUS_META };
export default FieldPicker;
